import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Progress, Spinner } from "reactstrap";
import * as moment from "moment";
import { getAgencyEngagement } from "common/services/analytics.service";

interface AgencyRow {
  agencyId: number;
  agencyName: string;
  visits: number;
  activeUsers: number;
  members: number;
  engagedShare: number;
  lastActiveAt: string | null;
}

/**
 * Which agencies are actually using the system. The existing report only
 * counted how many agencies were active, not how engaged each one is.
 */
const AgencyEngagement = ({ typeOfRequest }: { typeOfRequest: string }) => {
  const [rows, setRows] = useState<AgencyRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const response: any = await getAgencyEngagement({ typeOfRequest });
        if (cancelled) return;
        setRows(response?.data || []);
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [typeOfRequest]);

  return (
    <Col xxl={6}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Agency Engagement</h4>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-5">
              No agency activity for this period.
            </div>
          ) : (
            <div className="table-responsive table-card">
              <table className="table align-middle table-borderless table-nowrap mb-0">
                <thead className="text-muted table-light">
                  <tr>
                    <th scope="col">Agency</th>
                    <th scope="col">Visits</th>
                    <th scope="col">Users engaged</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.agencyId}>
                      <td>
                        <div className="fw-medium text-truncate">
                          {row.agencyName}
                        </div>
                        {row.lastActiveAt && (
                          <small className="text-muted">
                            Last active{" "}
                            {moment(new Date(row.lastActiveAt)).fromNow()}
                          </small>
                        )}
                      </td>
                      <td>{row.visits}</td>
                      <td style={{ minWidth: "120px" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-nowrap fs-12">
                            {row.activeUsers}
                            {row.members > 0 ? ` / ${row.members}` : ""}
                          </span>
                          {row.members > 0 && (
                            <Progress
                              value={row.engagedShare}
                              color={
                                row.engagedShare >= 60
                                  ? "success"
                                  : row.engagedShare >= 30
                                  ? "warning"
                                  : "danger"
                              }
                              className="flex-grow-1"
                              style={{ height: "5px" }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default AgencyEngagement;
